import { StrictMode } from 'react';
import { createRoot, type Root } from 'react-dom/client';
import { App } from './App';
// `?inline` returns the processed CSS as a string instead of injecting it into
// the document head, so we can inject it into the Shadow DOM ourselves below.
import css from './theme.css?inline';
import { ensureBrandFonts } from './fonts';

export interface MountHandle {
  unmount: () => void;
}

export interface MountOptions {
  /** Prefix for dataLayer event names. Unset: nothing is pushed. */
  eventPrefix?: string;
  /** Value for the sign-up link's `signup_ref` param. Unset: plain sign-up URL. */
  signupRef?: string;
}

function resolveTarget(target: string | HTMLElement): HTMLElement | null {
  if (typeof target === 'string') return document.querySelector<HTMLElement>(target);
  return target;
}

/**
 * Mount the calculator into `target`, isolated inside a Shadow DOM.
 *
 * The shadow root guarantees CSS isolation in both directions: the widget's
 * styles never leak to the host page, and the host page's styles cannot reach
 * the widget. React renders into a div inside that shadow root.
 */
export function mount(target: string | HTMLElement, options: MountOptions = {}): MountHandle {
  const host = resolveTarget(target);
  if (!host) {
    throw new Error(`LickdImpactCalculator.mount: target not found (${String(target)})`);
  }

  ensureBrandFonts();

  // Reuse an existing shadow root if the host already has one.
  const shadow = host.shadowRoot ?? host.attachShadow({ mode: 'open' });
  shadow.innerHTML = '';

  const style = document.createElement('style');
  style.textContent = css;
  shadow.appendChild(style);

  const container = document.createElement('div');
  shadow.appendChild(container);

  const root: Root = createRoot(container);
  root.render(
    <StrictMode>
      <App {...options} />
    </StrictMode>
  );

  return {
    unmount: () => {
      root.unmount();
      shadow.innerHTML = '';
    },
  };
}
